// #3711 — Figma read adapter: the admin's two controls, inside Settings →
// Tool Integrations (TENANT_ADMIN only, like the rest of that panel).
//
//   1. Connect a Figma personal access token. POST /tools/figma/connect checks
//      it with Figma (GET /v1/me) before saving; the response names the Figma
//      account and never echoes the token. A connected token shows in the
//      panel's connection list above ("Figma — <handle>") with Disconnect.
//   2. A project's allow-list of Figma files. The prototype intake refuses a
//      Figma file that is not on its project's list, before any call to Figma.
//
// Kept deliberately small (ruling 6 on #3711).
import { useState, useEffect } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import PasswordInput from '../PasswordInput';

interface Props {
  /** True when the tenant already has a connected FIGMA row. */
  connected: boolean;
  /** Called after a token is saved, so the host can reload its connection list. */
  onConnected: () => void;
}

interface ProjectRow { id: string; name: string }
interface FigmaFileRow { fileKey: string; addedAt: string }

const text: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' };
const input: React.CSSProperties = {
  flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
  background: 'var(--surface-alt)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 13,
};
const button: React.CSSProperties = {
  padding: '8px 14px', fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'white',
  background: '#1E1E1E', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

export default function FigmaConnectionSection({ connected, onConnected }: Props) {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectId, setProjectId] = useState('');
  const [files, setFiles] = useState<FigmaFileRow[]>([]);
  const [fileInput, setFileInput] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<ProjectRow[]>('/projects').then(setProjects).catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (!projectId) { setFiles([]); return; }
    let cancelled = false;
    setListError(null);
    apiFetch<{ files: FigmaFileRow[] }>(`/projects/${projectId}/figma-files`)
      .then((r) => { if (!cancelled) setFiles(r.files); })
      .catch((err) => { if (!cancelled) setListError(message(err, 'Could not load the Figma files.')); });
    return () => { cancelled = true; };
  }, [projectId]);

  const saveToken = async () => {
    setSaving(true);
    setTokenError(null);
    try {
      const r = await apiFetch<{ account: { handle: string | null; email: string | null } }>('/tools/figma/connect', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      setAccount([r.account.handle, r.account.email].filter(Boolean).join(' · ') || 'your Figma account');
      setToken('');
      onConnected();
    } catch (err) {
      setTokenError(message(err, 'Could not save the token.'));
    } finally {
      setSaving(false);
    }
  };

  const addFile = async () => {
    setListError(null);
    try {
      const r = await apiFetch<{ fileKey: string }>(`/projects/${projectId}/figma-files`, {
        method: 'POST',
        body: JSON.stringify({ file: fileInput }),
      });
      setFiles((prev) => (prev.some((f) => f.fileKey === r.fileKey)
        ? prev
        : [...prev, { fileKey: r.fileKey, addedAt: new Date().toISOString() }]));
      setFileInput('');
    } catch (err) {
      setListError(message(err, 'Could not add the file.'));
    }
  };

  const removeFile = async (fileKey: string) => {
    setListError(null);
    try {
      await apiFetch(`/projects/${projectId}/figma-files/${fileKey}`, { method: 'DELETE' });
      setFiles((prev) => prev.filter((f) => f.fileKey !== fileKey));
    } catch (err) {
      setListError(message(err, 'Could not remove the file.'));
    }
  };

  return (
    <div data-testid="figma-connection" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
        Figma
      </h3>
      <p style={{ ...text, marginBottom: 10 }}>
        {connected
          ? 'A Figma token is connected. Save a new one to replace it.'
          : 'Paste a Figma personal access token with read access. BluePrint checks it with Figma before saving.'}
      </p>
      {/* #3834 — the scopes the intake reads with; without library_content:read Figma refuses the components list. */}
      <p data-testid="figma-token-scopes" style={{ ...text, marginBottom: 10 }}>
        When you generate the token in Figma, give it these scopes: <code>current_user:read</code>,{' '}
        <code>file_content:read</code> and <code>library_content:read</code>. Add{' '}
        <code>file_variables:read</code> too if your Figma plan is Enterprise, so colour variables come in as design tokens.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <PasswordInput
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="figd_…"
            aria-label="Figma personal access token"
            autoComplete="off"
            style={{ ...input, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <button type="button" style={button} disabled={saving || token.trim() === ''} onClick={saveToken}>
          {saving ? 'Checking…' : connected ? 'Replace token' : 'Connect Figma'}
        </button>
      </div>
      {tokenError && <p role="alert" style={{ ...text, color: 'var(--error)', marginTop: 6 }}>{tokenError}</p>}
      {account && <p data-testid="figma-account" style={{ ...text, marginTop: 6 }}>Connected as {account}.</p>}

      <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '16px 0 6px' }}>
        Allowed Figma files by project
      </h4>
      <p style={{ ...text, marginBottom: 8 }}>
        A project can import only the Figma files listed here.
      </p>
      <select
        aria-label="Project"
        value={projectId}
        onChange={(e) => setProjectId(e.target.value)}
        style={{ ...input, flex: 'none', width: '100%', marginBottom: 8 }}
      >
        <option value="">Choose a project…</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {projectId && (
        <>
          {files.length === 0 && !listError && <p style={text}>No Figma files allowed yet.</p>}
          {files.map((f) => (
            <div key={f.fileKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
              <code style={{ fontSize: 12, color: 'var(--text)' }}>{f.fileKey}</code>
              <button
                type="button"
                onClick={() => removeFile(f.fileKey)}
                style={{ ...button, background: 'transparent', color: 'var(--error)', border: '1px solid var(--error)', padding: '4px 10px', fontSize: 12 }}
              >
                Remove
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              aria-label="Figma file link or key"
              value={fileInput}
              onChange={(e) => setFileInput(e.target.value)}
              placeholder="https://www.figma.com/design/…"
              style={input}
            />
            <button type="button" style={button} disabled={fileInput.trim() === ''} onClick={addFile}>Add</button>
          </div>
          {listError && <p role="alert" style={{ ...text, color: 'var(--error)', marginTop: 6 }}>{listError}</p>}
        </>
      )}
    </div>
  );
}
